import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { QueryObfuscator } from "../lib/queryObfuscation";
import { cacheConfig } from "./cache-config";
import {
  getClientHostPage,
  getClientSession,
  getClientVisitorId,
  HOST_PAGE_HEADER,
  SESSION_ID_HEADER,
  SESSION_KIND_BROWSER,
  SESSION_KIND_HEADER,
  VISITOR_ID_HEADER,
} from "../lib/clientSession";

interface CreateClientConfig {
  siteId: string;
  showError?: (message: string) => void;
  bridgeId?: number;
  bridgeType?: number;
  language?: string;
  override?: string;
  jsonHeaders?: Record<string, string>;
  getAuthToken?: () => string | null;
  // BE-only service-area dimension → `X-Service-Area` (traffic attribution T9).
  // A GETTER, not a static value: the booking session's area changes mid-session
  // (as the user picks pickup/dropoff) WITHOUT recreating this client, so it must
  // be read fresh on each request rather than captured once. Evaluated inside the
  // per-request authLink below; only the BE app supplies it, and it returns
  // undefined until an area is chosen (→ header omitted → "no area yet" bucket).
  getServiceAreaId?: () => string | undefined;
  // BE-only "Hosted at" hint → the framing page's own URL (the CMS/maFrame embed's
  // `ac[current_parent_url]`, persisted in FrameConfig). A GETTER read fresh per request
  // (like getServiceAreaId): getClientHostPage PREFERS it — it is the authoritative hosting
  // page and the only source that survives a cross-host parent (the norm), where the browser
  // blocks reading the parent's path — and only its absence falls back to best-effort
  // detection. Undefined for every non-BE app and for a non-embedded (direct) load.
  getHostPageParentUrl?: () => string | undefined;
  onAuthFailure?: () => void;
}

export function createApolloClient({ siteId, showError, bridgeId, bridgeType, language, override, jsonHeaders, getAuthToken, getServiceAreaId, getHostPageParentUrl, onAuthFailure }: CreateClientConfig) {
  // Track whether a token was ever present — used by both auth failure detection layers.
  // authFailureFired prevents duplicate calls when both layers detect the same failure.
  // lastToken tracks the token value so we can detect re-authentication (a different
  // token appearing) and reset authFailureFired for the next loss cycle.
  let hadToken = false;
  let authFailureFired = false;
  let lastToken: string | null = null;

  // Stable for the life of this tab. `persisted` marks a real per-tab sessionStorage
  // id — the browser client stamps the visit-eligible marker (T15) only then, so an
  // ephemeral fallback id (privacy mode) is never counted as a visit, and the SSR
  // client (which mints its own fresh uuid) never carries the marker at all.
  const clientSession = getClientSession();
  const sessionKind = clientSession.persisted ? SESSION_KIND_BROWSER : undefined;
  // The per-BROWSER visitor id (localStorage) a "visit" is counted on — one per
  // browser per origin across every tab and return; null when localStorage is
  // unavailable, in which case no header goes out and this browser is not counted.
  const visitorId = getClientVisitorId();

  // Create auth link to set headers
  const authLink = setContext((_, { headers: existingHeaders }) => {
    // Build headers that nAPI expects
    const headers: Record<string, string> = {
      ...existingHeaders,
      "X-Site": siteId,
      "X-Client-Name": process.env.NEXT_PUBLIC_APP_NAME || "",
      "X-Client-Version": process.env.NEXT_PUBLIC_APP_VERSION || "",
      [SESSION_ID_HEADER]: clientSession.id,
    };

    // Visit-eligibility marker (T15): present only when the session id is a persisted
    // per-tab id, so nAPI's traffic sensor counts a real visit — never an SSR render
    // or an ephemeral privacy-mode id.
    if (sessionKind) {
      headers[SESSION_KIND_HEADER] = sessionKind;
    }

    // Per-browser visitor id (T15): the element nAPI's visit sketch counts on. Sent only
    // when localStorage yielded one, so a browser without it sends nothing rather than a
    // fresh-per-call id that would inflate the distinct-visitor count.
    if (visitorId) {
      headers[VISITOR_ID_HEADER] = visitorId;
    }

    // BE-only service-area dimension (T9): the booking session's current area, read
    // fresh PER REQUEST (it changes mid-session as the user picks pickup/dropoff,
    // without recreating this client). Only the BE app supplies the getter; undefined
    // before an area is chosen → header omitted → the counter's "no area yet" bucket.
    const serviceAreaId = getServiceAreaId?.();
    if (serviceAreaId) {
      headers["X-Service-Area"] = serviceAreaId;
    }

    // The page HOSTING the app (T16), read fresh PER REQUEST because a single-page
    // navigation changes it without recreating this client — like the service area above.
    // Sent only when a value can be read; SSR / an undisclosable embed send nothing.
    // Prefer the BE-only parent-URL hint (the embed's own record of the framing page) over
    // best-effort browser detection; undefined for a direct/non-embedded load. See
    // getClientHostPage / getHostPageParentUrl.
    const hostPage = getClientHostPage(getHostPageParentUrl?.());
    if (hostPage) {
      headers[HOST_PAGE_HEADER] = hostPage;
    }

    // Add optional headers if provided
    if (language) {
      headers["X-Language"] = language;
    }
    
    if (override !== undefined && override !== null) {
      headers["X-Override"] = override;
    }
    
    if (jsonHeaders && Object.keys(jsonHeaders).length > 0) {
      headers["X-Json-Headers"] = JSON.stringify(jsonHeaders);
    }

    if (bridgeId !== undefined) {
      headers["X-Bridge-Id"] = String(bridgeId);
    }

    if (bridgeType !== undefined) {
      headers["X-Bridge-Type"] = String(bridgeType);
    }

    // Add Authorization header if getAuthToken is provided
    if (getAuthToken) {
      const token = getAuthToken();
      if (token) {
        // A different token value means the user re-authenticated — reset
        // authFailureFired so detection can fire on the next loss cycle.
        if (authFailureFired && token !== lastToken) {
          authFailureFired = false;
        }
        hadToken = true;
        lastToken = token;
        headers["Authorization"] = `Bearer ${token}`;
      } else if (hadToken && onAuthFailure && !authFailureFired) {
        // Proactive detection: token was previously present but now gone
        authFailureFired = true;
        onAuthFailure();
      }
    }

    return { headers };
  });

  // Custom fetch that handles obfuscation in production
  const customFetch: typeof fetch = (uri, options) => {
    if (process.env.NODE_ENV === 'production' && options?.body && typeof options.body === 'string') {
      try {
        const body = JSON.parse(options.body);
        
        // Handle both single and batch requests
        if (Array.isArray(body)) {
          // For batch requests, obfuscate each operation's entire payload
          const obfuscatedOps = body.map((op: any) => ({
            p: QueryObfuscator.obfuscate(JSON.stringify(op))
          }));
          options.body = JSON.stringify(obfuscatedOps);
        } else {
          // For single requests, obfuscate the entire payload
          const obfuscatedPayload = QueryObfuscator.obfuscate(JSON.stringify(body));
          options.body = JSON.stringify({ p: obfuscatedPayload });
        }
      } catch (e) {
      }
    }
    
    return fetch(uri, options);
  };

  // Reactive auth failure detection: inspect response data for "User not logged in".
  // This catches cases where the token exists but is expired/invalid — the error
  // arrives as HTTP 200 data, not a GraphQL error, so onError never sees it.
  const authFailureLink = onAuthFailure
    ? new ApolloLink((operation, forward) => {
        return forward(operation).map((response) => {
          if (hadToken && response.data) {
            const data = response.data as Record<string, unknown>;
            const hasAuthError = Object.values(data).some(
              (value) =>
                value !== null &&
                typeof value === "object" &&
                (value as Record<string, unknown>).error === "User not logged in",
            );

            if (hasAuthError && !authFailureFired) {
              authFailureFired = true;
              onAuthFailure();
            } else if (!hasAuthError && authFailureFired) {
              // Successful response after a previous auth failure — the user
              // re-authenticated (possibly with a token of the same string
              // value). Reset so the next auth loss can be detected.
              authFailureFired = false;
            }
          }
          return response;
        });
      })
    : null;

  return new ApolloClient({
    link: ApolloLink.from([
      authLink,
      ...(authFailureLink ? [authFailureLink] : []),
      onError(({ operation, response, graphQLErrors, networkError }) => {
        if (networkError) {
          console.error("Apollo network error", networkError);
          console.error("operation", operation);
          console.error("response", response);
          if (showError)
            showError("Network error occurred while fetching data");
        }

        if (graphQLErrors) {
          console.error("Apollo graphQL error", graphQLErrors);
          console.error("operation", operation);
          console.error("response", response);
          if (showError) {
            const errorMessage =
              graphQLErrors[0]?.message ||
              "An error occurred while fetching data";
            showError(errorMessage);
          }
        }
      }),
      new RetryLink({
        attempts: {
          max: 5,
          retryIf: (error) => !!error,
        },
        delay: {
          initial: 2000,
          max: 5000,
          jitter: true,
        },
      }),
      new BatchHttpLink({
        uri: process.env.NEXT_PUBLIC_NAPI_CLIENT_URL,
        credentials: "include",
        fetch: customFetch,
      }),
    ]),
    cache: new InMemoryCache(cacheConfig),
  });
}